import { Mood, TextComponentStyle } from "@glide/component-utils";
import { UIAspect, UIImageFill, UIImageStyle, UISize } from "@glide/wire";
import * as t from "io-ts";

// io-ts codecs for renderable description validation

const moodCodec = t.union([
    t.literal(Mood.Default),
    t.literal(Mood.Neutral),
    t.literal(Mood.Warning),
    t.literal(Mood.Danger),
    t.literal(Mood.Success),
]);

const textStyleCodec = t.union([
    t.literal(TextComponentStyle.large),
    t.literal(TextComponentStyle.regular),
    t.literal(TextComponentStyle.small),
    t.literal(TextComponentStyle.footnote),
    t.literal(TextComponentStyle.metaText),
    t.literal(TextComponentStyle.headlineXSmall),
    t.literal(TextComponentStyle.headlineSmall),
    t.literal(TextComponentStyle.headlineMedium),
    t.literal(TextComponentStyle.headlineLarge),
    t.literal(TextComponentStyle.headlineXLarge),
]);

const imageStyleCodec = t.union([
    t.literal(UIImageStyle.Rectilinear),
    t.literal(UIImageStyle.Circle),
]);

const imageAspectRatioCodec = t.union([
    t.literal(UIAspect.Auto),
    t.literal(UIAspect.NineBySixteen),
    t.literal(UIAspect.Square),
    t.literal(UIAspect.FourByThree),
    t.literal(UIAspect.SixteenByNine),
    t.literal(UIAspect.ThreeByOne),
]);

const imageSizeCodec = t.union([
    t.literal(UISize.Small),
    t.literal(UISize.Medium),
    t.literal(UISize.Full),
]);

const imageFillCodec = t.union([
    t.literal(UIImageFill.Cover),
    t.literal(UIImageFill.Contain),
]);

const bigNumbersItemCodec = t.partial({
    name: t.string,
    value: t.string,
    description: t.string,
});

export const bigNumbersDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("big-numbers"),
        items: t.array(bigNumbersItemCodec),
    }),
    t.partial({
        title: t.string,
    }),
]);

export const hintDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("hint"),
        text: t.string,
    }),
    t.partial({
        mood: moodCodec,
    }),
]);

export const imageDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("image"),
        image: t.string,
    }),
    t.partial({
        style: imageStyleCodec,
        size: imageSizeCodec,
        fullWidthOnMobile: t.boolean,
        aspectRatio: imageAspectRatioCodec,
        zoomable: t.boolean,
        imageFill: imageFillCodec,
    }),
]);

const listItemCodec = t.partial({
    emphasis: t.string,
    title: t.string,
    subtitle: t.string,
    image: t.string,
});

export const listDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("list"),
        items: t.array(listItemCodec),
    }),
    t.partial({
        title: t.string,
    }),
]);

export const richTextDescriptionCodec = t.type({
    kind: t.literal("rich-text"),
    text: t.string,
});

export const textDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("text"),
        text: t.string,
    }),
    t.partial({
        style: textStyleCodec,
    }),
]);

export const tableDescriptionCodec = t.intersection([
    t.type({
        kind: t.literal("table"),
        items: t.array(t.record(t.string, t.union([t.string, t.number]))),
    }),
    t.partial({
        title: t.string,
    }),
]);

export const componentRenderableDescriptionCodec = t.union([
    bigNumbersDescriptionCodec,
    hintDescriptionCodec,
    imageDescriptionCodec,
    listDescriptionCodec,
    richTextDescriptionCodec,
    textDescriptionCodec,
    tableDescriptionCodec,
]);

export type ComponentRenderableDescription = t.TypeOf<typeof componentRenderableDescriptionCodec>;

export const renderableDescriptionsCodec = t.array(componentRenderableDescriptionCodec);

export type BigNumbersRenderableDescription = t.TypeOf<typeof bigNumbersDescriptionCodec>;
export type HintRenderableDescription = t.TypeOf<typeof hintDescriptionCodec>;
export type ImageRenderableDescription = t.TypeOf<typeof imageDescriptionCodec>;
export type ListRenderableDescription = t.TypeOf<typeof listDescriptionCodec>;
export type RichTextRenderableDescription = t.TypeOf<typeof richTextDescriptionCodec>;
export type TextRenderableDescription = t.TypeOf<typeof textDescriptionCodec>;
export type TableRenderableDescription = t.TypeOf<typeof tableDescriptionCodec>;
